import React from 'react';

// Form primitives matching the app's established control look
// (`rounded border border-slate-300 px-2 py-1`), with the design bundle's
// label/hint/error anatomy. Background stays transparent so controls sit on
// paper or card surfaces in both themes.

const CONTROL =
  'rounded border border-slate-300 bg-transparent px-2 py-1 ' +
  'placeholder:text-slate-400 disabled:bg-slate-50 disabled:text-slate-400';

interface FieldChrome {
  label?: string;
  hint?: string;
  error?: string;
  /** JetBrains Mono — keys, codes, URLs. */
  mono?: boolean;
  size?: 'sm' | 'md';
}

function chromeClasses({ mono, size, error }: FieldChrome): string {
  return [
    CONTROL,
    size === 'sm' ? 'text-xs' : 'text-sm',
    mono ? 'font-mono' : '',
    error ? 'border-red-300' : '',
  ].join(' ');
}

function Wrap({
  label,
  hint,
  error,
  children,
}: FieldChrome & { children: React.ReactNode }) {
  if (!label && !hint && !error) return <>{children}</>;
  return (
    <label className="block">
      {label && (
        <span className="mb-1 block text-sm font-medium text-slate-700">{label}</span>
      )}
      {children}
      {(error || hint) && (
        <span className={`mt-1 block text-xs ${error ? 'text-red-700' : 'text-slate-500'}`}>
          {error || hint}
        </span>
      )}
    </label>
  );
}

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    FieldChrome {}

export function Input({ label, hint, error, mono, size, className, ...rest }: InputProps) {
  return (
    <Wrap label={label} hint={hint} error={error}>
      <input
        className={[chromeClasses({ mono, size, error }), className ?? ''].join(' ')}
        {...rest}
      />
    </Wrap>
  );
}

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'>,
    FieldChrome {}

export function Select({
  label,
  hint,
  error,
  mono,
  size,
  className,
  children,
  ...rest
}: SelectProps) {
  return (
    <Wrap label={label} hint={hint} error={error}>
      <select
        className={[chromeClasses({ mono, size, error }), 'bg-card', className ?? ''].join(' ')}
        {...rest}
      >
        {children}
      </select>
    </Wrap>
  );
}

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: React.ReactNode;
  description?: string;
}

export function Checkbox({ label, description, className, ...rest }: CheckboxProps) {
  const box = (
    <input
      type="checkbox"
      className={['h-3.5 w-3.5 shrink-0 accent-blue-600', className ?? ''].join(' ')}
      {...rest}
    />
  );
  if (!label) return box;
  return (
    <label className="flex items-start gap-1.5 text-sm">
      <span className="flex h-5 items-center">{box}</span>
      <span>
        {label}
        {description && <span className="block text-xs text-slate-500">{description}</span>}
      </span>
    </label>
  );
}
